import {
  DEFAULT_THICKNESS,
  Element,
  LEGACY_ANCHOR_BY_TYPE,
  ThicknessAnchor,
} from "./index";

const parseLegacyStrokeWidth = (raw: unknown): number => {
  if (raw === undefined || raw === null) return DEFAULT_THICKNESS;
  const parsed = Number(String(raw).replace("px", ""));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_THICKNESS;
};

/**
 * Migrates the legacy presentational `style.strokeWidth` onto the first-class
 * `thickness` / `thicknessAnchor` properties. Pure, scale-free and idempotent:
 * an already-normalised element is returned unchanged apart from a defensive
 * strip of `style.strokeWidth`.
 */
export const normalizeElementThickness = (element: Element): Element => {
  const legacyStrokeWidth = element.style?.strokeWidth;

  // A present strokeWidth always wins: it is either legacy persisted data (the
  // element has no thickness yet) or a fresh write from a control that has not
  // been migrated to writing `thickness` directly.
  const thickness =
    legacyStrokeWidth !== undefined
      ? parseLegacyStrokeWidth(legacyStrokeWidth)
      : typeof element.thickness === "number" && element.thickness >= 0
        ? element.thickness
        : DEFAULT_THICKNESS;

  const thicknessAnchor =
    element.thicknessAnchor ?? LEGACY_ANCHOR_BY_TYPE[element.type];

  if (
    legacyStrokeWidth === undefined &&
    element.thickness === thickness &&
    element.thicknessAnchor === thicknessAnchor
  ) {
    return element;
  }

  const normalized = {
    ...element,
    thickness,
    thicknessAnchor,
  } as Element;

  if (legacyStrokeWidth !== undefined) {
    const style = { ...normalized.style };
    delete style.strokeWidth;
    normalized.style = style;
  }

  return normalized;
};

/**
 * Thickness of an element, falling back for any element that has not passed
 * through the normaliser (e.g. hand-built test fixtures).
 */
export const getThickness = (element: { thickness?: number }): number =>
  typeof element.thickness === "number" && element.thickness >= 0
    ? element.thickness
    : DEFAULT_THICKNESS;

/**
 * ThicknessAnchor. Falls back to legacy if undefined for elements on older
 * canvases.
 */
export const getThicknessAnchor = (element: Element): ThicknessAnchor =>
  element.thicknessAnchor ?? LEGACY_ANCHOR_BY_TYPE[element.type];
